import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import {
  BellIcon,
  EnvelopeIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const NotificationSettings = () => {
  const { user } = useAuth();
  const { settings, loading, updateNotificationSettings } = useNotifications();
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  // Sync localSettings with settings when they change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Admins and coaches don't have notification settings - redirect to profile
  if (user?.role === 'admin' || user?.role === 'coach') {
    return <Navigate to="/profile" replace />;
  }

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateNotificationSettings(localSettings);
      if (result.success) {
        toast.success('Notification settings updated successfully');
      } else {
        toast.error(result.error || 'Failed to update settings');
        setLocalSettings(settings); // Revert on error
      }
    } catch (error) {
      toast.error('Failed to update settings');
      setLocalSettings(settings); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalSettings(settings);
  };

  const hasChanges = JSON.stringify(localSettings) !== JSON.stringify(settings);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full w-12 h-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 notification-settings">
      {/* Header */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-3">
            <BellIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary">Notification Settings</h1>
            <p className="text-secondary mt-1">
              Manage how you receive notifications and updates
            </p>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="space-y-8">
          {/* Email Notifications Section */}
          <div>
            <div className="flex items-center mb-4">
              <EnvelopeIcon className="w-5 h-5 text-primary mr-3" />
              <h2 className="text-lg font-semibold text-primary">Email Notifications</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg">
                <div>
                  <h3 className="font-medium text-primary">Enable Email Notifications</h3>
                  <p className="text-sm text-secondary">Receive notifications via email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localSettings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* User Notifications Section */}
          <div>
              <div className="flex items-center mb-4">
                <BellIcon className="w-5 h-5 text-green-600 mr-3" />
                <h2 className="text-lg font-semibold text-primary">User Notifications</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg">
                  <div>
                    <h3 className="font-medium text-primary">Low Concession Alerts</h3>
                    <p className="text-sm text-secondary">Get notified when you have 0 concessions or reach the -2 limit</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={localSettings.lowConcessionAlerts}
                      onChange={(e) => handleSettingChange('lowConcessionAlerts', e.target.checked)}
                      disabled={!localSettings.emailNotifications}
                      className="sr-only peer disabled:opacity-50"
                    />
                    <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                      localSettings.lowConcessionAlerts && localSettings.emailNotifications
                        ? 'bg-green-600 peer-checked:after:translate-x-full'
                        : 'bg-gray-200'
                    }`}></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg">
                  <div>
                    <h3 className="font-medium text-primary">Manual Booking Notifications</h3>
                    <p className="text-sm text-secondary">Get notified when admin adds or removes you from classes</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={localSettings.manualBookingNotifications}
                      onChange={(e) => handleSettingChange('manualBookingNotifications', e.target.checked)}
                      disabled={!localSettings.emailNotifications}
                      className="sr-only peer disabled:opacity-50"
                    />
                    <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                      localSettings.manualBookingNotifications && localSettings.emailNotifications
                        ? 'bg-blue-600 peer-checked:after:translate-x-full'
                        : 'bg-gray-200'
                    }`}></div>
                  </label>
                </div>


                <div className="flex items-center justify-between p-4 rounded-lg">
                  <div>
                    <h3 className="font-medium text-primary">Concession Addition Notifications</h3>
                    <p className="text-sm text-secondary">Get notified when admin adds concessions to your account</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={localSettings.concessionAdditionNotifications}
                      onChange={(e) => handleSettingChange('concessionAdditionNotifications', e.target.checked)}
                      disabled={!localSettings.emailNotifications}
                      className="sr-only peer disabled:opacity-50"
                    />
                    <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                      localSettings.concessionAdditionNotifications && localSettings.emailNotifications
                        ? 'bg-yellow-600 peer-checked:after:translate-x-full'
                        : 'bg-gray-200'
                    }`}></div>
                  </label>
                </div>
              </div>
            </div>

          {/* Information Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="w-5 h-5 text-primary mr-2 mt-0.5" />
              <div className="text-sm text-primary">
                <p className="font-medium">About Notifications</p>
                <p className="mt-1">
                  Email notifications help you stay informed about concession alerts and account status. 
                  Booking confirmations and cancellations are handled through in-app notifications only. 
                  You can customize which types of email notifications you receive.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-secondary mb-4 sm:mb-0">
            {hasChanges && (
              <span className="text-orange-600 font-medium">
                You have unsaved changes
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="btn-secondary"
            >
              Reset Changes
            </button>
            
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;



